import nameModelValidator from 'forms/shared/validators/models/name/name';
import locationBirthplaceWithoutCountyModelValidator from 'forms/shared/validators/models/location/models/birthplaceWithoutCounty';
import relationshipsSharedForeignBornDocumentValidator from 'forms/sections/relationships/shared/foreignBornDocument';
import { DEFAULT_EARLIEST, DEFAULT_LATEST, OTHER } from 'constants/dateLimits';
import { getSafeDateTime, setToStartOfMonth } from 'helpers/date';
import { ValidateJSModel } from 'forms/shared/types/validatejs';
import { BranchCollectionModel } from 'forms/shared/types';
import { DateBaseValidatorOptions } from 'forms/shared/validators/base/date/types';
import { SanitizeDateRange } from 'forms/shared/validators/models/dateRange/instance';
import { DeepSafePartial } from 'types/types';
import { countryString } from '../../../../../validators/location';
import { RelationshipsCohabitantItemModel, RelationshipsCohabitantOtherNameModel } from '../types';
import { relationshipsCohabitantItemOtherNameItemValidator } from './fields';

export const hasAliasDuplicates = (value: DeepSafePartial<BranchCollectionModel<RelationshipsCohabitantOtherNameModel>>) => {
  let result = false;
  if (value?.items) {
    const cleanItems = value.items.map((x) => x?.Item);
    // remove the type pollution for validation from the date range
    // there may be more type pollution possible that wasn't available during the testing
    cleanItems.forEach((v) => {
      const today = new Date();
      if (v) {
        const dates = SanitizeDateRange(v?.DatesUsed);
        if (dates?.present && dates?.to) {
          dates.to.day = today.getDate().toString();
          dates.to.year = today.getFullYear().toString();
          dates.to.month = (today.getMonth() + 1).toString();
        }
        v.DatesUsed = { ...dates }; // eslint-disable-line no-param-reassign
      }
    });
    // compare items on the list case insensitive and ignore Reason, Has, and index differences.
    const items = cleanItems.map((x) => JSON.stringify({
      ...x, Reason: '', index: '', Has: ''
    }).toLowerCase());
    const uniqueItems = new Set(items);
    result = !(items.length === uniqueItems.size);
  }
  return result;
};

export const relationshipsCohabitantItemValidator: ValidateJSModel<RelationshipsCohabitantItemModel> = {
  Name: {
    presence: true,
    model: { validator: nameModelValidator },
  },

  Birthdate: {
    presence: true,
    date: { ...OTHER, tooEarly: 'error.birthdateTooFar.min' },
  },

  BirthPlace: {
    presence: true,
    location: { validator: locationBirthplaceWithoutCountyModelValidator },
  },

  SSN: {
    presence: true,
    ssn: true,
  },

  Citizenship: {
    presence: true,
    country: true,
    hasValue: true
  },

  OtherNames: (value, attributes) => {
    const dateLimits: DateBaseValidatorOptions = {
      earliest: DEFAULT_EARLIEST,
      latest: DEFAULT_LATEST,
    };
    if (attributes?.Birthdate) {
      dateLimits.earliest = setToStartOfMonth(attributes?.Birthdate);
    }
    if (hasAliasDuplicates(value)) {
      return {
        presence: true,
        length: { is: -2, message: 'error.cohabitant.duplicateAlias' },
        branchCollection: {
          validator: relationshipsCohabitantItemOtherNameItemValidator,
          ...dateLimits,
          tooEarly: 'error.cohabitant.min',
        },
      };
    }
    return {
      presence: true,
      branchCollection: {
        validator: relationshipsCohabitantItemOtherNameItemValidator,
        ...dateLimits,
        tooEarly: 'error.cohabitant.min',
      },
    };
  },

  CohabitationBegan: (value, attributes, attributeName, options) => {
    const { Birthdate } = attributes;
    const { applicantBirthdate } = options;

    const cohabitantBirthdatetime = getSafeDateTime(Birthdate);
    const applicantBirthdatetime = getSafeDateTime(applicantBirthdate);

    let earliestDate = null;
    let message = null;

    // Provide validation and error messaging based on the latest valid date requirement that needs to be met
    if ((cohabitantBirthdatetime.valid && !applicantBirthdatetime.valid)
      || ((cohabitantBirthdatetime.datetime && applicantBirthdatetime.datetime)
        && cohabitantBirthdatetime?.datetime > applicantBirthdatetime?.datetime)) {
      earliestDate = cohabitantBirthdatetime;
      message = 'error.cohabitant.min';
    } else {
      earliestDate = applicantBirthdatetime;
      message = 'error.cohabitant.applicantmin';
    }

    return {
      presence: true,
      date: {
        requireYear: false,
        earliest: earliestDate?.datetime,
        tooEarly: message,
        latest: DEFAULT_LATEST,
      },
    };
  },

  ForeignBornDocument: (value, attributes) => {
    if (
      attributes?.BirthPlace
      && attributes?.BirthPlace.country
      && countryString(attributes?.BirthPlace.country) !== 'United States'
    ) {
      return {
        presence: true,
        model: { validator: relationshipsSharedForeignBornDocumentValidator },
      };
    }

    return {};
  },
  Comments: {},
  ContinueComments: {},
};
